import { useState } from 'react';
import { IconChevronDown, IconPencil, IconPlayerPause, IconPlayerPlay, IconTrash } from '@tabler/icons-react';
import {
  IconButton,
  Popover,
  RowActionPill,
  RowMenu,
  RowMenuDivider,
  RowMenuItem,
  Stack,
  StatusDot,
  Text,
  Tooltip,
} from '@wapharma/design-system';
import { LifecycleTimeline, type LifecycleStage } from './LifecycleTimeline';
import rowStyles from './rowActionStyles.module.css';

export function ActiveStatusDot({ active, size = 10 }: { active: boolean; size?: number }) {
  return (
    <StatusDot
      tone={active ? 'success' : 'muted'}
      size={size}
      title={active ? 'Actif' : 'Inactif'}
      aria-label={active ? 'Actif' : 'Inactif'}
    />
  );
}

function LifecycleTooltipBody({ stages }: { stages: LifecycleStage[] }) {
  return (
    <div style={{ padding: 10, maxWidth: 360 }}>
      <Stack gap={8}>
        <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
          Parcours compte
        </Text>
        <LifecycleTimeline stages={stages} hideTags panelInk />
      </Stack>
    </div>
  );
}

function StatusAdornment({
  active,
  lifecycleStages,
}: {
  active: boolean;
  lifecycleStages?: LifecycleStage[] | null;
}) {
  const dot = <ActiveStatusDot active={active} size={10} />;
  if (!lifecycleStages?.length) return dot;
  return (
    <Tooltip wide portal position="bottom" label={<LifecycleTooltipBody stages={lifecycleStages} />}>
      {dot}
    </Tooltip>
  );
}

export function ResourceActionPill({
  label,
  active,
  onEdit,
  onToggleActive,
  onDelete,
  canToggleActive = true,
  toggleBusy = false,
  lifecycleStages,
}: {
  label: string;
  active: boolean;
  onEdit: () => void;
  onToggleActive: () => void;
  onDelete?: () => void;
  canToggleActive?: boolean;
  toggleBusy?: boolean;
  lifecycleStages?: LifecycleStage[] | null;
}) {
  const [opened, setOpened] = useState(false);

  return (
    <Popover
      opened={opened}
      onChange={setOpened}
      portal
      align="end"
      target={
        <span
          data-datatable-rowclick-ignore
          onClick={(e) => e.stopPropagation()}
          className={rowStyles.wrap}
        >
          <RowActionPill
            responsive
            disabled={toggleBusy}
            primary={
              <button
                type="button"
                aria-label={`Modifier ${label}`}
                disabled={toggleBusy}
                aria-busy={toggleBusy || undefined}
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit();
                }}
                className={rowStyles.primaryBtn}
              >
                <StatusAdornment active={active} lifecycleStages={lifecycleStages} />
                <span className={rowStyles.iconWrap}>
                  <IconPencil size={16} stroke={1.8} aria-hidden style={{ display: 'block' }} />
                </span>
              </button>
            }
            menu={
              <IconButton
                type="button"
                size="xs"
                variant="ghost"
                aria-label={`Plus d'actions ${label}`}
                className={rowStyles.menuBtn}
                onClick={(e) => {
                  e.stopPropagation();
                  setOpened((v) => !v);
                }}
              >
                <IconChevronDown size={16} stroke={1.8} aria-hidden />
              </IconButton>
            }
          />
        </span>
      }
    >
      <RowMenu aria-label={`Actions — ${label}`} style={{ minWidth: 200 }}>
        <RowMenuItem
          onClick={() => {
            setOpened(false);
            onEdit();
          }}
        >
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <IconPencil size={14} stroke={1.8} aria-hidden /> Modifier
          </span>
        </RowMenuItem>
        {canToggleActive ? (
          <RowMenuItem
            disabled={toggleBusy}
            onClick={() => {
              setOpened(false);
              if (!toggleBusy) onToggleActive();
            }}
          >
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              {active ? (
                <IconPlayerPause size={14} stroke={1.8} aria-hidden />
              ) : (
                <IconPlayerPlay size={14} stroke={1.8} aria-hidden />
              )}
              {active ? 'Désactiver' : 'Activer'}
            </span>
          </RowMenuItem>
        ) : (
          <RowMenuItem disabled>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              <ActiveStatusDot active={active} size={8} />
              Votre compte — activation verrouillée
            </span>
          </RowMenuItem>
        )}
        {onDelete ? (
          <>
            <RowMenuDivider />
            <RowMenuItem
              danger
              onClick={() => {
                setOpened(false);
                onDelete();
              }}
            >
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <IconTrash size={14} stroke={1.8} aria-hidden /> Supprimer
              </span>
            </RowMenuItem>
          </>
        ) : null}
      </RowMenu>
    </Popover>
  );
}
